// ShareConversationSheet — ONE share experience used by BOTH the public Q&A
// ("Share this conversation") and the AI Assistant's saved-bookmark "Share".
// Jun 2026 (Randy):
//   • SAVE-FIRST — if the thread isn't saved yet, save it silently so it gets a
//     permanent ?bookmark=<id> link that can carry audio.
//   • AUDIO (rule B) — if the saved audio is ALREADY complete, no choices: just
//     share with the green "audio included" badge. If it's partial/none, the user
//     chooses to "Complete it & include audio (~X¢)" or "Send without audio".
//   • SEED ALWAYS RIDES ALONG — every link (conversation OR a blank silo) carries
//     the sharer's saved seed identity (?ref=<their code>) so attribution sticks.
//   • TWO-VOICE — coverage/finish use the same two-voice plan as live playback
//     (question voice + answer voice), so a shared conversation behaves identically
//     no matter where it was shared from.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useVoiceQA, VOICE_OPTIONS, chunkForSpeech } from "@/lib/useVoiceQA";
import { getRefSig } from "@/lib/attribution";
import { ShareNetwork, Copy, EnvelopeSimple, X, CheckCircle, SpeakerHigh, CircleNotch } from "@phosphor-icons/react";

const isAi = (m) => m?.role === "assistant" || m?.role === "ai";

export default function ShareConversationSheet({ slug, siloName = "this silo", messages = [], bookmarkId = null, answerVoice, onClose, onSaved }) {
  const av = answerVoice && VOICE_OPTIONS.some((o) => o.id === answerVoice) ? answerVoice : (VOICE_OPTIONS[0]?.id || "onyx");
  const qv = useMemo(() => {
    const i = Math.max(0, VOICE_OPTIONS.findIndex((o) => o.id === av));
    return VOICE_OPTIONS[(i + 1) % VOICE_OPTIONS.length].id;
  }, [av]);

  const realMessages = useMemo(
    () => (messages || []).filter((m) => (m.text || "").trim() && !m.error),
    [messages],
  );
  const hasConversation = realMessages.length > 0;
  const plan = useMemo(
    () => realMessages.map((m, i) => ({ seg_idx: i, voice: isAi(m) ? av : qv, text: m.text, chunks: chunkForSpeech(m.text) })),
    [realMessages, av, qv],
  );
  const autoTitle = useMemo(() => {
    const firstUser = realMessages.find((m) => !isAi(m));
    const base = (firstUser?.text || realMessages[0]?.text || "Shared conversation").replace(/\s+/g, " ").trim();
    return base.slice(0, 60) || "Shared conversation";
  }, [realMessages]);

  const voice = useVoiceQA({
    slug,
    ask: async () => null,
    getVoiceId: () => av,
    voiceReady: true,
    onSelfNoFunding: () => toast.error("Add an OpenAI key or fund a voucher to include the audio."),
    onGuardBlocked: () => toast.error("Voice is blocked — check your key or voucher."),
  });

  const [stage, setStage] = useState("init"); // init|saving|checking|choice|finishing|complete|ready|blank|cap|error
  const [bmId, setBmId] = useState(bookmarkId || "");
  const [sharerCode, setSharerCode] = useState("");
  const [miss, setMiss] = useState({ idxs: [], count: 0, cents: 0 });
  const [copied, setCopied] = useState(false);

  const seedQs = sharerCode ? `?ref=${encodeURIComponent(sharerCode)}` : "";
  const shareUrl = (hasConversation && bmId)
    ? `${window.location.origin}/s/${slug}${seedQs}${seedQs ? "&" : "?"}bookmark=${encodeURIComponent(bmId)}`
    : `${window.location.origin}/s/${slug}${seedQs}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Seed identity — ALWAYS, even for a blank-canvas silo share.
      let code = "";
      try { const r = await api.get("/me/referral-link"); code = r.data?.referral_code || ""; } catch { /* */ }
      if (!code) { try { code = getRefSig() || ""; } catch { /* */ } }
      if (cancelled) return;
      setSharerCode(code);

      if (!hasConversation) { setStage("blank"); return; }

      // Save-first.
      let id = bookmarkId || "";
      if (!id) {
        setStage("saving");
        try {
          const turns = realMessages.map((m) => ({ role: isAi(m) ? "ai" : "user", text: m.text }));
          const res = await api.post("/bookmarks", { slug, section_name: "Shared conversations", file_title: autoTitle, messages: turns });
          id = res.data?.id || "";
          if (id) onSaved?.(id);
        } catch (e) {
          if (cancelled) return;
          const c = e?.response?.data?.detail?.code;
          if (c === "SUBSCRIBER_CAP_REACHED" || c === "CREATOR_CAP_REACHED") { setStage("cap"); return; }
          setStage("error"); return;
        }
      }
      if (cancelled || !id) { if (!cancelled) setStage("error"); return; }
      setBmId(id);

      // Audio coverage (two-voice).
      setStage("checking");
      // Jun 2026 (Randy) — "already-listened" fix. Pull any audio ALREADY in the
      // 24h cache (e.g. from a live voice chat the sharer just had) into this
      // bookmark's 7-day store FIRST. attach-audio only COPIES cached audio (no
      // synthesis, no charge), so the share never asks the sharer to re-pay for
      // parts they already voiced. Whatever's genuinely missing still surfaces below.
      try {
        await api.post(`/bookmarks/${id}/attach-audio`, {
          segments: plan.map((s) => ({ seg_idx: s.seg_idx, voice: s.voice, chunks: s.chunks })),
        });
      } catch { /* non-fatal — coverage below still drives the choice */ }
      try {
        const cov = await api.post(`/bookmarks/${id}/audio-coverage`, {
          segments: plan.map((s) => ({ seg_idx: s.seg_idx, chunk_count: s.chunks.length })),
        });
        if (cancelled) return;
        if (cov.data?.audio_complete) { setStage("complete"); return; }
        const idxs = cov.data?.missing_seg_idxs || [];
        const missSegs = plan.filter((s) => idxs.includes(s.seg_idx));
        const cents = missSegs.reduce((n, s) => n + s.text.replace(/\s+/g, " ").trim().length * 0.0015, 0);
        setMiss({ idxs, count: missSegs.length, cents });
        setStage("choice");
      } catch { if (!cancelled) setStage("ready"); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completeAndInclude = async () => {
    setStage("finishing");
    const toWarm = plan.filter((s) => miss.idxs.includes(s.seg_idx));
    for (const s of toWarm) { try { await voice.warmCached(s.text, s.voice); } catch { /* */ } }
    try {
      const r = await api.post(`/bookmarks/${bmId}/attach-audio`, {
        segments: plan.map((s) => ({ seg_idx: s.seg_idx, voice: s.voice, chunks: s.chunks })),
      });
      if (r.data?.audio_complete) { toast.success("Audio finished — it plays free for whoever opens this."); setStage("complete"); }
      else { toast("Saved the audio I could — add an OpenAI key/voucher to finish the rest.", { icon: "↪" }); setStage("ready"); }
    } catch { toast.error("Couldn't finish the audio."); setStage("ready"); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Link copied."); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Could not copy the link."); }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: siloName, text: `Check out ${siloName} on My Silo`, url: shareUrl }); }
      catch (e) { if (e.name !== "AbortError") toast.error("Share failed."); }
    } else copyLink();
  };
  const emailShare = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent("Check out " + siloName)}&body=${encodeURIComponent(`Thought you'd enjoy this — ${siloName}:\n\n${shareUrl}`)}`;
  };

  const busy = stage === "init" || stage === "saving" || stage === "checking" || stage === "finishing";
  const showLink = stage === "complete" || stage === "ready" || stage === "blank";

  const linkRow = (
    <div className="space-y-3" data-testid="share-sheet-link-area">
      <div className="flex items-center gap-2 rounded-lg border hairline bg-[#FDFCF8] px-3 py-2">
        <span className="flex-1 truncate text-xs text-[#4A5D4E]" data-testid="share-sheet-url">{shareUrl}</span>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={copyLink} data-testid="share-sheet-copy"
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] transition-colors">
          {copied ? <CheckCircle size={14} weight="fill" /> : <Copy size={14} />} {copied ? "Copied" : "Copy link"}
        </button>
        <button type="button" onClick={nativeShare} data-testid="share-sheet-native"
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-white border hairline text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors">
          <ShareNetwork size={14} weight="duotone" /> Share…
        </button>
        <button type="button" onClick={emailShare} data-testid="share-sheet-email"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-white border hairline text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors">
          <EnvelopeSimple size={14} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/45 px-4" data-testid="share-conversation-sheet" onClick={onClose}>
      <div className="modal-safe bg-white rounded-2xl shadow-xl border border-[#E5E5E0] max-w-md w-full p-6 relative text-[#1C1B1A]" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} data-testid="share-sheet-close"
          className="absolute top-3.5 right-3.5 text-[#9b9a96] hover:text-[#1C1B1A] p-1" title="Close"><X size={18} /></button>
        <div className="flex items-center gap-2 text-[16px] font-semibold pr-6">
          <ShareNetwork size={18} weight="duotone" className="text-[#4A5D4E]" />
          {hasConversation ? "Share this conversation" : "Share this silo"}
        </div>

        {busy && (
          <div className="mt-5 flex items-center gap-2 text-sm text-[#6B6A68]" data-testid="share-sheet-busy">
            <CircleNotch size={16} className="animate-spin text-[#4A5D4E]" />
            {stage === "saving" ? "Saving this conversation…" : stage === "finishing" ? "Recording the missing audio…" : "Getting it ready…"}
          </div>
        )}

        {stage === "cap" && (
          <div className="mt-4" data-testid="share-sheet-cap">
            <p className="text-sm text-[#6B6A68]">You've reached your saved-conversation limit on this silo. Remove a saved one, or upgrade to a Creator account, then try sharing again.</p>
            <div className="mt-4 flex justify-end"><button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-[#1C1B1A] text-white text-sm font-semibold">Got it</button></div>
          </div>
        )}

        {stage === "error" && (
          <div className="mt-4" data-testid="share-sheet-error">
            <p className="text-sm text-[#6B6A68]">Something went wrong getting the share ready. Please try again.</p>
            <div className="mt-4 flex justify-end"><button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-[#1C1B1A] text-white text-sm font-semibold">Close</button></div>
          </div>
        )}

        {stage === "choice" && (
          <div className="mt-4" data-testid="share-sheet-choice">
            <p className="text-sm text-[#6B6A68]">
              {miss.count} {miss.count === 1 ? "part hasn't" : "parts haven't"} been voiced yet. Include the audio so the whole conversation plays <span className="font-semibold text-[#27AE60]">free</span> for whoever you send it to?
            </p>
            <div className="mt-4 space-y-2">
              <button type="button" onClick={completeAndInclude} data-testid="share-sheet-complete-audio"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#27AE60] text-white text-sm font-semibold hover:bg-[#229954] transition-colors">
                <SpeakerHigh size={16} weight="fill" /> Complete it &amp; include audio
                <span className="text-[11px] bg-white/20 rounded px-1.5 py-0.5">~{miss.cents < 1 ? miss.cents.toFixed(1) : Math.round(miss.cents)}¢</span>
              </button>
              <button type="button" onClick={() => setStage("ready")} data-testid="share-sheet-skip-audio"
                className="w-full py-2.5 rounded-full border border-[#E5E5E0] text-[#6B6A68] text-sm font-semibold hover:text-[#1C1B1A] hover:border-[#4A5D4E] transition-colors">
                Send without audio
              </button>
            </div>
          </div>
        )}

        {stage === "complete" && (
          <div className="mt-4 space-y-4" data-testid="share-sheet-complete">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#27AE60] bg-[#27AE60]/10 rounded-full px-3 py-1.5" data-testid="share-sheet-audio-badge">
              <SpeakerHigh size={13} weight="fill" /> Audio included — plays free for anyone who opens this
            </div>
            {linkRow}
          </div>
        )}

        {stage === "ready" && (
          <div className="mt-4 space-y-3" data-testid="share-sheet-ready">
            <p className="text-xs text-[#6B6A68]">Shared as text — whoever opens it gets the exact conversation and can listen by covering the audio themselves.</p>
            {linkRow}
          </div>
        )}

        {stage === "blank" && (
          <div className="mt-4 space-y-3" data-testid="share-sheet-blank">
            <p className="text-xs text-[#6B6A68]">Send {siloName}'s page to someone new. They open to a blank canvas and can start asking right away — and your invite credit rides along with this link.</p>
            {linkRow}
          </div>
        )}
      </div>
    </div>
  );
}
